import React, { useMemo } from "react";

type XY = { x: number; y: number };
type Placed = { x: number; y: number; scale: number };

type PencilMotion = {
    /** start offset t.o.v. screen center (px in computer-space) */
    start?: Partial<XY>;
    /** end offset t.o.v. screen center (px in computer-space) */
    end?: Partial<XY>;
    /** rotatie van het potlood (deg) */
    rotationDeg?: number;
    /** schaal van het potlood (svg scale factor) */
    scale?: number;
};

type GearConfig = {
    /** schaal van het tandwiel (css scale factor) */
    scale?: number;
};

type LineConfig = {
    /** Bezier points als offsets t.o.v. screen center (px) */
    start?: Partial<XY>;
    c1?: Partial<XY>;
    c2?: Partial<XY>;
    end?: Partial<XY>;
};

export type AutomationLayout = {
    computer?: Partial<Placed>;
    contentOffset?: Partial<XY>;

    pencil?: PencilMotion;
    gear?: GearConfig;
    line?: LineConfig;
};

export type AutomationSceneProps = {
    className?: string;
    style?: React.CSSProperties;
    playing?: boolean;
    showBackground?: boolean;
    monitorFrameColor?: string;
    layout?: AutomationLayout;
};

const DEFAULTS = {
    computer: {
        x: 200, y: 0, scale: 2

    } as Placed,
    contentOffset: { x: 0, y: 0 } as XY,

    // Potlood: 230° gedraaid, beweegt over het scherm
    pencil: {
        start: { x: -50, y: 45 },
        end: { x: 25, y: 45 },
        rotationDeg: 215,
        scale: 0.5,
    } as Required<PencilMotion>,

    // Tandwiel in het midden
    gear: {
        scale: 1,
    } as Required<GearConfig>,

    // Lijn: volledig instelbaar via bezier offsets t.o.v. screen center
    line: {
        start: { x: -96, y: 45 },
        c1: { x: -60, y: 8 },
        c2: { x: 40, y: 45 },
        end: { x: 90, y: 15 },
    } as Required<LineConfig>,
};

export default function AutomationScene({
    className,
    style,
    playing = true,
    showBackground = false,
    monitorFrameColor = "rgba(100, 100, 100, 0.90)",
    layout,
}: AutomationSceneProps) {
    const uid = useMemo(() => `auto_${Math.random().toString(16).slice(2)}`, []);

    const L = useMemo(() => {
        const computer = { ...DEFAULTS.computer, ...(layout?.computer ?? {}) };
        const contentOffset = { ...DEFAULTS.contentOffset, ...(layout?.contentOffset ?? {}) };

        const pencil: Required<PencilMotion> = {
            ...DEFAULTS.pencil,
            ...(layout?.pencil ?? {}),
            start: { ...DEFAULTS.pencil.start, ...(layout?.pencil?.start ?? {}) },
            end: { ...DEFAULTS.pencil.end, ...(layout?.pencil?.end ?? {}) },
        };

        const gear: Required<GearConfig> = { ...DEFAULTS.gear, ...(layout?.gear ?? {}) };

        const line: Required<LineConfig> = {
            ...DEFAULTS.line,
            ...(layout?.line ?? {}),
            start: { ...DEFAULTS.line.start, ...(layout?.line?.start ?? {}) },
            c1: { ...DEFAULTS.line.c1, ...(layout?.line?.c1 ?? {}) },
            c2: { ...DEFAULTS.line.c2, ...(layout?.line?.c2 ?? {}) },
            end: { ...DEFAULTS.line.end, ...(layout?.line?.end ?? {}) },
        };

        return { computer, contentOffset, pencil, gear, line };
    }, [layout]);

    // Screen rect (lokale coords binnen computer group)
    const screen = { x: 18, y: 58, w: 224, h: 128, r: 10 };
    const screenCx = screen.x + screen.w / 2 + L.contentOffset.x;
    const screenCy = screen.y + screen.h / 2 + L.contentOffset.y;

    // Potlood start/end absolute in screen space
    const pencilStart = { x: screenCx + L.pencil.start.x, y: screenCy + L.pencil.start.y };
    const pencilEnd = { x: screenCx + L.pencil.end.x, y: screenCy + L.pencil.end.y };

    // Gear midden
    const gearPos = { x: screenCx, y: screenCy };

    const lineD = makeLinePath(screenCx, screenCy, L.line);

    return (
        <div className={className} style={{ width: "100%", height: "100%", ...style }}>
            <style>{css(uid, playing)}</style>

            <svg
                viewBox="0 0 900 420"
                preserveAspectRatio="xMidYMid meet"
                style={{ width: "100%", height: "100%", display: "block" }}
                role="img"
                aria-label="Niet tekenen, maar automatiseren"
            >
                <defs>
                    <clipPath id={`${uid}_screenClip`}>
                        <rect x={screen.x} y={screen.y} width={screen.w} height={screen.h} rx={screen.r} />
                    </clipPath>

                    <linearGradient id={`${uid}_screenGrad`} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0" stopColor="rgba(120, 210, 255, 0.18)" />
                        <stop offset="1" stopColor="rgba(120, 210, 255, 0.05)" />
                    </linearGradient>

                    <filter id={`${uid}_softGlow`} x="-30%" y="-30%" width="160%" height="160%">
                        <feGaussianBlur stdDeviation="4" result="b" />
                        <feMerge>
                            <feMergeNode in="b" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {showBackground && <rect x="0" y="0" width="900" height="420" rx="24" className={`${uid}_bg`} />}

                {/* ✅ COMPUTER / MONITOR (hergebruik) */}
                <g transform={`translate(${L.computer.x}, ${L.computer.y}) scale(${L.computer.scale})`}>
                    <rect
                        x="0"
                        y="40"
                        width="260"
                        height="180"
                        rx="18"
                        fill={monitorFrameColor}
                        stroke="rgba(255,255,255,0.10)"
                        strokeWidth={2}
                    />
                    <rect x="115" y="220" width="30" height="30" rx="10" fill={monitorFrameColor} opacity={0.95} />
                    <rect x="70" y="248" width="120" height="18" rx="9" fill={monitorFrameColor} opacity={0.95} />

                    {/* Screen base */}
                    <rect x={screen.x} y={screen.y} width={screen.w} height={screen.h} rx={screen.r} className={`${uid}_screen`} />
                    <rect x={screen.x} y={screen.y} width={screen.w} height={screen.h} rx={screen.r} fill={`url(#${uid}_screenGrad)`} opacity={0.9} />

                    {/* ✅ Inhoud (geclipt binnen scherm) */}
                    <g clipPath={`url(#${uid}_screenClip)`}>
                        <circle cx={screenCx + 18} cy={screenCy - 26} r="110" className={`${uid}_pulse`} />

                        {/* lijn verschijnt binnen het scherm */}
                        <path d={lineD} className={`${uid}_line`} fill="none" />

                        {/* tandwiel (blijft op plek, draait, vervaagt aan einde) */}
                        <g
                            className={`${uid}_gearWrap`}
                            style={
                                {
                                    ["--gx" as any]: `${gearPos.x}px`,
                                    ["--gy" as any]: `${gearPos.y}px`,
                                    ["--gscale" as any]: L.gear.scale,
                                } as React.CSSProperties
                            }
                            filter={`url(#${uid}_softGlow)`}
                        >
                            <g className={`${uid}_gearSpin`}>
                                <g transform={`scale(${L.gear.scale})`}>
                                    <path
                                        d={gearPath(8, 34, 46, 0.55)}
                                        fill="rgba(120,200,255,0.18)"
                                        stroke="rgba(210,240,255,0.55)"
                                        strokeWidth="4"
                                        strokeLinejoin="round"
                                    />
                                    <circle cx="0" cy="0" r="16" fill="rgba(0,0,0,0.35)" stroke="rgba(210,240,255,0.6)" strokeWidth="4" />
                                    <circle cx="0" cy="0" r="5" fill="rgba(210,240,255,0.65)" />
                                </g>
                            </g>
                        </g>

                        {/* POTLOOD (230° gedraaid) beweegt over het scherm */}
                        <g
                            className={`${uid}_pencilWrap`}
                            style={
                                {
                                    ["--psx" as any]: `${pencilStart.x}px`,
                                    ["--psy" as any]: `${pencilStart.y}px`,
                                    ["--pex" as any]: `${pencilEnd.x}px`,
                                    ["--pey" as any]: `${pencilEnd.y}px`,
                                    ["--prot" as any]: `${L.pencil.rotationDeg}deg`,
                                } as React.CSSProperties
                            }
                        >
                            <g transform={`scale(${L.pencil.scale})`}>
                                {/* jouw potlood (zonder <image>) */}
                                <rect
                                    style={{
                                        fill: "#ffcb62",
                                        fillOpacity: 1,
                                        stroke: "#666666",
                                        strokeWidth: 0,
                                        strokeLinecap: "round",
                                        strokeMiterlimit: 4,
                                        strokeDasharray: "none",
                                        paintOrder: "fill markers stroke",
                                    }}
                                    width="132.28346"
                                    height="37.795273"
                                    x="0"
                                    y="-50"
                                />
                                <path
                                    style={{
                                        fill: "#e7ded4",
                                        fillOpacity: 1,
                                        stroke: "#000000",
                                        strokeWidth: 0,
                                        strokeLinecap: "round",
                                        strokeLinejoin: "round",
                                        strokeMiterlimit: 4,
                                        strokeDasharray: "none",
                                        strokeOpacity: 1,
                                    }}
                                    d="m 132.28346,-12.204726 v -37.795272 l 37.79527,15.33077 -37.79527,22.464502"
                                />
                                <path
                                    style={{
                                        fill: "#000000",
                                        stroke: "#000000",
                                        strokeWidth: 0,
                                        strokeLinecap: "butt",
                                        strokeLinejoin: "miter",
                                        strokeMiterlimit: 4,
                                        strokeDasharray: "none",
                                        strokeOpacity: 1,
                                    }}
                                    d="m 170.07873,-34.669228 -12.69187,-5.18671 0.0291,12.694038 12.66278,-7.507328"
                                />
                            </g>
                        </g>
                    </g>

                    <rect x={screen.x} y={screen.y} width={screen.w} height={screen.h} rx={screen.r} className={`${uid}_screenGlow`} />
                </g>
            </svg>
        </div>
    );
}

function makeLinePath(cx: number, cy: number, cfg: Required<LineConfig>) {
    const s = cfg.start;
    const c1 = cfg.c1;
    const c2 = cfg.c2;
    const e = cfg.end;

    return `M ${cx + s.x} ${cy + s.y}
    C ${cx + c1.x} ${cy + c1.y},
      ${cx + c2.x} ${cy + c2.y},
      ${cx + e.x} ${cy + e.y}`;
}

function gearPath(
    teeth: number,
    rInner: number,
    rOuter: number,
    toothDepth: number
) {
    // teeth: aantal tanden (bv 10–14)
    // rInner: basis radius (tussen tanden)
    // rOuter: buiten radius (top van tanden)
    // toothDepth: breedte van tand "flat" (0..1), bv 0.55

    const pts: Array<[number, number]> = [];
    const step = (Math.PI * 2) / teeth;

    // tandvorm per tooth: 4 punten (inner -> outer -> outer -> inner)
    for (let i = 0; i < teeth; i++) {
        const a = i * step;

        const a0 = a - step * (toothDepth * 0.5); // begin buitenvlak
        const a1 = a + step * (toothDepth * 0.5); // einde buitenvlak

        const aMidL = a - step * 0.5;
        const aMidR = a + step * 0.5;

        // inner valley links (tussen tanden)
        pts.push([Math.cos(aMidL) * rInner, Math.sin(aMidL) * rInner]);

        // outer tooth face
        pts.push([Math.cos(a0) * rOuter, Math.sin(a0) * rOuter]);
        pts.push([Math.cos(a1) * rOuter, Math.sin(a1) * rOuter]);

        // inner valley rechts
        pts.push([Math.cos(aMidR) * rInner, Math.sin(aMidR) * rInner]);
    }

    return `M ${pts.map((p) => `${p[0].toFixed(2)} ${p[1].toFixed(2)}`).join(" L ")} Z`;
}

function css(uid: string, playing: boolean) {
    const run = playing ? "running" : "paused";

    return `
    .${uid}_bg { fill: rgba(10, 12, 18, 0.92); }

    .${uid}_screen {
      fill: rgba(40, 60, 110, 0.72);
      stroke: rgba(255,255,255,0.12);
      stroke-width: 2;
    }
    .${uid}_screenGlow {
      fill: none;
      stroke: rgba(120, 210, 255, 0.22);
      stroke-width: 6;
      opacity: 0.65;
    }

    .${uid}_pulse {
      fill: rgba(120,200,255,0.45);
      opacity: 0;
      animation: ${uid}_pulse 5200ms cubic-bezier(.2,.8,.2,1) infinite;
      animation-play-state: ${run};
      pointer-events: none;
    }

    .${uid}_pencilWrap,
    .${uid}_gearWrap,
    .${uid}_gearSpin {
      transform-box: fill-box;
      transform-origin: center;
    }

    /* Potlood eerst */
    .${uid}_pencilWrap {
      animation: ${uid}_pencilMove 5200ms cubic-bezier(.2,.8,.2,1) infinite;
      animation-play-state: ${run};
      will-change: transform, opacity;
      opacity: 0;
    }
    @keyframes ${uid}_pencilMove {
      0%   { transform: translate(var(--psx), var(--psy)) rotate(var(--prot)); opacity: 0; }
      6%   { opacity: 1; }
      32%  { transform: translate(var(--pex), var(--pey)) rotate(var(--prot)); opacity: 1; }
      62%  { opacity: 0; }
      100% { opacity: 0; transform: translate(var(--pex), var(--pey)) rotate(var(--prot)); }
    }

    /* Dan lijn */
    .${uid}_line {
      stroke: rgba(210,240,255,0.85);
      stroke-width: 6;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-dasharray: 520;
      stroke-dashoffset: 520;
      opacity: 0;
      animation: ${uid}_lineShow 5200ms cubic-bezier(.2,.8,.2,1) infinite;
      animation-play-state: ${run};
      pointer-events: none;
    }
    @keyframes ${uid}_lineShow {
      0%, 50% { stroke-dashoffset: 520; opacity: 0; }
      32% { opacity: 1; }
      48% { stroke-dashoffset: 0; opacity: 1; }
      62% { opacity: 0; }
      100% { opacity: 0; stroke-dashoffset: 0; }
    }

    /* Gear: blijft op plek, draait, vervaagt */
    .${uid}_gearWrap {
      opacity: 0;
      animation: ${uid}_gearFade 5200ms cubic-bezier(.2,.8,.2,1) infinite;
      animation-play-state: ${run};
      will-change: opacity;
      transform: translate(var(--gx), var(--gy));
    }
    @keyframes ${uid}_gearFade {
      0%, 50% { opacity: 0; }
      76%     { opacity: 1; }
      92%     { opacity: 1; }
      100%    { opacity: 0; }
    }

    .${uid}_gearSpin {
      animation: ${uid}_spin 2200ms linear infinite;
      animation-play-state: ${run};
      will-change: transform;
    }
    @keyframes ${uid}_spin {
      to { transform: rotate(360deg); }
    }

    @keyframes ${uid}_pulse {
      0%, 25% { opacity: 0; }
      40% { opacity: 0.10; }
      58% { opacity: 0.03; }
      100% { opacity: 0; }
    }
  `;
}